import { useEffect, useState } from "react";
import { plus } from "../../asset";
import CustomButton from "../../components/CustomButton";
import AddEditQuestions from "./AddEditQuestions";
import Tag from "../../components/Tags";
import queryFieldControllerApis from "../../api/queryFieldControllerApis";
import { FormField } from "../../types/Query";
import { schoolCode } from "../../util/Constants";
import ConfirmationModal from "../../components/modal/ConfirmationModal";
import { useBranchCode } from "../../util/hooks/useBranch";
import useConfirmToast from "../../util/hooks/useConfirmToast";
import DragableLayout from "../../components/layouts/DragableLayout";
import SortableList from "../../components/common/SortableItem";

export default function AddedQuestions() {
  const [formFields, setFormFields] = useState<FormField[]>([]);
  const [editingFieldId, setEditingFieldId] = useState<number | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const { confirm, modal } = useConfirmToast();
  const branchCode = useBranchCode();

  const loadFields = async () => {
    const res = await queryFieldControllerApis.getFormFields(branchCode);
    if (res?.data) setFormFields(res.data);
  };

  useEffect(() => {
    loadFields();
  }, []);

  const handleSave = async (data: FormField) => {
    if (isAdding) {
      // ADD CUSTOM NEW FIELD
      await queryFieldControllerApis.addCustomField(branchCode, data);
      setIsAdding(false);
    } else if (editingFieldId) {
      // UPDATE EXISTING FIELD
      await queryFieldControllerApis.updateField(editingFieldId, data);
      setEditingFieldId(null);
    }

    await loadFields();
  };

  const handleDelete = async (id: number) => {
    const isConfirm = await confirm(
      "Are you sure you want to delete this Question?",
    );

    if (!isConfirm) return;

    await queryFieldControllerApis.deleteField(id);

    await loadFields(); 
  };

  const handleOrderChange = async (newList: FormField[]) => {
    const updated = newList.map((item, index) => ({
      ...item,
      seqId: index + 1,
    }));

    setFormFields(updated);

    // for (const item of updated) {
    //   await queryFieldControllerApis.updateField(item.id, {
    //     ...item,
    //     seqId: item.seqId,
    //   });
    // }
  };

  return (
    <div className="gap-10 flex flex-col ">
      <SortableList
        items={formFields}
        onOrderChange={handleOrderChange}
        renderItem={(q, dragProps) => (
          <DragableLayout
            setNodeRef={dragProps.ref}
            listeners={dragProps.listeners}
            attributes={dragProps.attributes}
          >
            {editingFieldId === q.id ? (
              <AddEditQuestions
                editingData={q}
                mode="FIELD"
                onSave={handleSave}
                onClose={() => setEditingFieldId(null)}
              />
            ) : (
              <div className="flex justify-between items-center w-full">
                <div className="flex gap-11 items-start">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={q.isSelected}
                    onChange={async () => {
                      await queryFieldControllerApis.updateField(q.id, {
                        ...q,
                        isSelected: !q.isSelected,
                      });
                      await loadFields();
                    }}
                  />

                  <div className="flex flex-col gap-2">
                    <span className="text-sm font-semibold text-neutral-400">
                      {q.fieldName}
                    </span>

                    <span className="text-sm text-neutral-400">
                      Required: {q.isFieldRequired ? "Yes" : "No"}
                    </span>

                    {q.options.length > 0 && (
                      <span className="text-sm text-neutral-400 flex flex-wrap gap-2 capitalize">
                        tags:
                        {q.options.map((c, index) => (
                          <Tag key={index} text={c} color="primary" />
                        ))}
                      </span>
                    )}

                    <span className="text-sm font-medium text-neutral-200">
                      Accepts {q.fieldType}
                    </span>
                  </div>
                </div>

                <div className="flex gap-2">
                  <button
                    className="bg-warning-500 rounded-full px-4 py-2 text-xs font-semibold text-white"
                    onClick={() => setEditingFieldId(q.id)}
                  >
                    Edit
                  </button>
                  <button
                    className="bg-error-500 rounded-full px-4 py-2 text-xs font-semibold text-white"
                    onClick={() => handleDelete(q.id)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            )}
          </DragableLayout>
        )}
      />

      {isAdding && (
        <AddEditQuestions
          mode="FIELD"
          onSave={handleSave}
          editingData={null}
          onClose={() => setIsAdding(false)}
        />
      )}

      {modal}

      {!isAdding && editingFieldId === null && (
        <CustomButton
          color="bg-success-600"
          text="Add Custom Question"
          icon={plus}
          onClick={() => setIsAdding(true)}
        />
      )}

      {showConfirm && (
        <ConfirmationModal
          title="Are you sure you want to remove this question from the form?"
          confirmText="Yes"
          cancelText="No"
          onClose={() => setShowConfirm(false)}
        />
      )}
    </div>
  );
}
